import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import {
  IMPORT_STATE,
  type ImportHistoryItemT,
  type OkendoInfo,
} from '../types';

const mapOkendoInfo = (data: unknown): OkendoInfo | null => {
  if (!data || typeof data !== 'object') return null;

  const info = data as {
    workspace_id?: string;
    alias?: string;
    state?: IMPORT_STATE;
    import_state?: IMPORT_STATE;
    history?: ImportHistoryItemT[];
  };

  if (!info.alias) return null;

  return {
    workspace_id: info.workspace_id || info.alias,
    alias: info.alias,
    import_state: info.import_state || info.state || IMPORT_STATE.NOT_STARTED,
    history: info.history || [],
  };
};

export const useOkendo = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceAlias = currWorkspace?.alias;

  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const queryKey = getIntegrationQueryKeys(SOURCE_TYPE.OKENDO, workspaceAlias);

  const { data: okendoWorkspace } = useQuery({
    queryKey,
    queryFn: async () =>
      mapOkendoInfo(await service.okendo.getOkendoItem(workspaceAlias)),
    enabled,
  });

  const okendoAlias = okendoWorkspace?.alias ?? '';

  const { mutate: disconnectOkendoMutation } = useMutation({
    mutationFn: () => service.okendo.deleteOkendoInfo(okendoAlias),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: ({ start_date, end_date }: NullableDateRangeT) => {
      if (!start_date || !end_date) return Promise.resolve(false);
      const startDate: string = start_date;
      const endDate: string = end_date;
      return service.okendo.importWorkspaceFeedback(
        okendoAlias,
        startDate,
        endDate,
      );
    },
    onSuccess: () => {
      queryClient.setQueryData<OkendoInfo | null>(queryKey, (current) => {
        if (!current) return current;

        return {
          ...current,
          import_state: IMPORT_STATE.IMPORTING,
        };
      });
    },
  });

  return {
    okendoWorkspace,
    disconnectOkendoMutation,
    requestImportMutation,
    setOkendoWorkspace: (workspace: OkendoInfo) => {
      queryClient.setQueryData<OkendoInfo | null>(queryKey, workspace);
    },
  };
};
